import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Save, IndianRupee, Plus } from "lucide-react";
import api, { errMsg } from "@/lib/api";

const STATUSES = ["confirmed", "in_progress", "completed", "cancelled", "expired", "payment_pending"];
const CUR = "\u20b9";
const fmt = (n) => (n === null || n === undefined ? "—" : `${CUR}${Number(n).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`);

export default function AdminBookingDetail() {
  const { id } = useParams();
  const [b, setB] = useState(null);
  const [status, setStatus] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  // Phase 4 — record payment form
  const [payAmount, setPayAmount] = useState("");
  const [payMethod, setPayMethod] = useState("offline");
  const [payRef, setPayRef] = useState("");
  const [payNote, setPayNote] = useState("");
  const [recording, setRecording] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/bookings/${id}`);
      setB(data);
      setStatus(data.status);
      setNotes(data.admin_notes || "");
    } catch (e) { toast.error(errMsg(e, "Load failed")); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const save = async () => {
    setSaving(true);
    try {
      const payload = {};
      if (status !== b.status) payload.status = status;
      if (notes !== (b.admin_notes || "")) payload.admin_notes = notes;
      if (Object.keys(payload).length === 0) { toast.info("No changes"); setSaving(false); return; }
      const { data } = await api.patch(`/admin/bookings/${id}`, payload);
      setB(data); setStatus(data.status); setNotes(data.admin_notes || "");
      toast.success("Booking updated");
    } catch (e) { toast.error(errMsg(e, "Update failed")); }
    finally { setSaving(false); }
  };

  const recordPayment = async () => {
    const amount = Number(payAmount);
    if (!amount || amount <= 0) { toast.error("Enter a positive ₹ amount"); return; }
    setRecording(true);
    try {
      const { data } = await api.post(`/admin/bookings/${id}/record-payment`, {
        amount,
        method: payMethod || "offline",
        reference: payRef || null,
        note: payNote || null,
      });
      setB(data.booking);
      setStatus(data.booking.status);
      setPayAmount(""); setPayRef(""); setPayNote("");
      toast.success(`Recorded ${fmt(amount)} · payment ${data.booking.payment_status}`);
    } catch (e) { toast.error(errMsg(e, "Record failed")); }
    finally { setRecording(false); }
  };

  if (loading || !b) return <div className="text-sm text-muted-foreground">Loading…</div>;

  const remaining = Math.max(0, Number(b.final_amount || 0) - Number(b.amount_paid || 0));
  const payments = b.payments || [];

  return (
    <div className="mx-auto max-w-4xl">
      <Link to="/admin/bookings" data-testid="admin-booking-back" className="mb-4 inline-flex items-center gap-1 text-sm font-semibold text-primary hover:underline">
        <ArrowLeft className="h-4 w-4" /> Back to bookings
      </Link>

      <div className="rounded-3xl border border-primary/30 bg-primary/5 p-6">
        <span className="kbd-eyebrow">Booking</span>
        <div className="mt-2 font-serif-display text-4xl font-semibold text-secondary" data-testid="admin-booking-ref">{b.reference}</div>
        <div className="mt-1 text-xs uppercase tracking-[0.16em] text-muted-foreground">
          Customer: <span className="font-semibold text-secondary">{b.customer_id?.slice(0, 8)}…</span>
        </div>
      </div>

      <section className="surface-card mt-6">
        <div className="text-[11px] font-semibold uppercase tracking-[0.2em] text-accent">Snapshot</div>
        <dl className="mt-3 grid gap-2 text-sm sm:grid-cols-2">
          <Info label="Event" v={b.event_type} />
          <Info label="Date" v={b.event_date} />
          <Info label="Time" v={b.event_time} />
          <Info label="Guests" v={b.guest_count} />
          <Info label="Venue" v={b.venue} full />
          <Info label="Package" v={b.package_snapshot?.name} full />
          {b.gst_snapshot?.enabled && (
            <Info label={`GST (${b.gst_snapshot.rate}%) on ${"\u20b9"}${Number(b.gst_snapshot.taxable_subtotal).toLocaleString("en-IN")}`} v={<span data-testid="admin-booking-gst">{fmt(b.gst_snapshot.gst_amount)}</span>} />
          )}
          <Info label="Final amount" v={fmt(b.final_amount)} />
          <Info label="Advance requested" v={fmt(b.advance_requested)} />
          <Info label="Amount paid" v={<span data-testid="admin-booking-amount-paid">{fmt(b.amount_paid)}</span>} />
          <Info label="Payment status" v={<span data-testid="admin-booking-payment-status">{b.payment_status}</span>} />
        </dl>
      </section>

      <section className="surface-card mt-6" data-testid="admin-record-payment-section">
        <div className="flex items-center justify-between">
          <div className="text-[11px] font-semibold uppercase tracking-[0.2em] text-accent">Record payment received</div>
          <div className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground">
            Remaining: <span data-testid="admin-booking-remaining" className="font-semibold text-secondary">{fmt(remaining)}</span>
          </div>
        </div>
        <p className="mt-1 text-[11px] text-muted-foreground">
          Explicit ₹ amount only — never a percentage. Online payments are stubbed; enter what the customer paid offline.
        </p>
        <div className="mt-3 grid gap-3 sm:grid-cols-[140px_140px_1fr_auto]">
          <label className="block text-xs">
            <span className="mb-1 block font-semibold uppercase tracking-[0.14em] text-muted-foreground">Amount ₹</span>
            <div className="flex items-center rounded-xl border border-border/70 bg-background px-2">
              <IndianRupee className="h-3.5 w-3.5 text-muted-foreground" />
              <input
                data-testid="admin-record-amount"
                type="number" min="0" step="0.01"
                value={payAmount}
                onChange={(e) => setPayAmount(e.target.value)}
                className="w-full bg-transparent px-2 py-2 text-sm outline-none"
                placeholder="e.g. 25000"
              />
            </div>
          </label>
          <label className="block text-xs">
            <span className="mb-1 block font-semibold uppercase tracking-[0.14em] text-muted-foreground">Method</span>
            <select data-testid="admin-record-method" value={payMethod} onChange={(e) => setPayMethod(e.target.value)}
                    className="w-full rounded-xl border border-border/70 bg-background px-2 py-2 text-sm">
              <option value="offline">offline</option>
              <option value="upi">upi</option>
              <option value="bank_transfer">bank transfer</option>
              <option value="cash">cash</option>
              <option value="cheque">cheque</option>
            </select>
          </label>
          <label className="block text-xs">
            <span className="mb-1 block font-semibold uppercase tracking-[0.14em] text-muted-foreground">Reference (optional)</span>
            <input data-testid="admin-record-reference" value={payRef} onChange={(e) => setPayRef(e.target.value)}
                   placeholder="UPI txn id / cheque no." className="w-full rounded-xl border border-border/70 bg-background px-3 py-2 text-sm" />
          </label>
          <button data-testid="admin-record-save" onClick={recordPayment} disabled={recording || remaining <= 0}
                  className="btn-primary self-end !py-2 !text-xs disabled:cursor-not-allowed disabled:opacity-50">
            <Plus className="h-3.5 w-3.5" /> {recording ? "Recording…" : "Record"}
          </button>
        </div>
        <label className="mt-3 block text-xs">
          <span className="mb-1 block font-semibold uppercase tracking-[0.14em] text-muted-foreground">Note (optional)</span>
          <input data-testid="admin-record-note" value={payNote} onChange={(e) => setPayNote(e.target.value)}
                 placeholder="Internal note" className="w-full rounded-xl border border-border/70 bg-background px-3 py-2 text-sm" />
        </label>

        <div className="mt-5" data-testid="admin-payments-list">
          <div className="text-[10px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">Payment history</div>
          {payments.length === 0 ? (
            <div className="mt-2 rounded-xl border border-dashed border-border/70 bg-background p-3 text-xs text-muted-foreground" data-testid="admin-payments-empty">
              No payments recorded yet.
            </div>
          ) : (
            <ul className="mt-2 divide-y divide-border/60">
              {payments.map((p, i) => (
                <li key={p.id || i} data-testid={`admin-payment-row-${i}`} className="flex flex-wrap items-center justify-between gap-2 py-2 text-xs">
                  <div>
                    <div className="font-semibold text-secondary">{fmt(p.amount)}</div>
                    <div className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">
                      {(p.recorded_at || "").slice(0, 16).replace("T", " ")} · {p.method || "offline"}
                    </div>
                  </div>
                  {p.reference && <div className="text-[10px] text-muted-foreground">ref: {p.reference}</div>}
                  {p.note && <div className="max-w-md truncate text-[11px] text-secondary/80">{p.note}</div>}
                </li>
              ))}
            </ul>
          )}
        </div>
      </section>

      <section className="surface-card mt-6">
        <div className="text-[11px] font-semibold uppercase tracking-[0.2em] text-accent">Manage</div>
        <label className="mt-3 block text-sm">
          <span className="mb-1 block text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">Status</span>
          <select data-testid="admin-booking-status" value={status} onChange={(e) => setStatus(e.target.value)} className="w-full rounded-xl border border-border/70 bg-background px-4 py-2">
            {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </label>
        <label className="mt-3 block text-sm">
          <span className="mb-1 block text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">Admin notes</span>
          <textarea data-testid="admin-booking-notes" rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} className="w-full rounded-xl border border-border/70 bg-background px-4 py-2 text-sm" />
        </label>
        <div className="mt-4 flex justify-end">
          <button data-testid="admin-booking-save" onClick={save} disabled={saving} className="btn-primary">
            <Save className="h-4 w-4" /> {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </section>
    </div>
  );
}

function Info({ label, v, full }) {
  return <div className={full ? "sm:col-span-2" : ""}><div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">{label}</div><div className="mt-0.5 font-medium text-secondary">{v || "—"}</div></div>;
}
